/**
 * Phase 167.b — Per-Academy Digest Opt-Out Preferences
 *
 * Lightweight self-serve page for academy owners to disable the weekly
 * scout digest email. Reached via the email footer link
 * `/admin/scouting/digest/preferences` and the in-app Settings panel.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Mail, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function DigestPreferences() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/scouting/digest/preferences');
      setEnabled(!!r.data?.digest_enabled);
      setUpdatedAt(r.data?.updated_at || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load preferences');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const r = await api.put('/admin/scouting/digest/preferences', { digest_enabled: !!next });
      setEnabled(!!r.data?.digest_enabled);
      toast.success(r.data?.digest_enabled
        ? 'Weekly scout digest re-enabled.'
        : 'Weekly scout digest disabled — you won\'t receive Monday emails.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="phase167b-prefs-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <Link
          to="/admin/scouting"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900"
          data-testid="phase167b-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to scouting
        </Link>

        <Card>
          <CardHeader>
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl">Weekly Scout Digest Preferences</CardTitle>
                <CardDescription className="mt-1 text-xs">
                  Control whether your academy receives the Monday-morning "Your Week in Scouting" email.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {loading ? (
              <div className="flex items-center gap-2 text-slate-500 py-6">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading preferences…
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4">
                  <div>
                    <div className="font-bold text-slate-900 text-sm">Weekly Scout Heatmap Digest</div>
                    <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                      A branded recap of your scouting telemetry — total profile views, top
                      cities discovering your athletes, the most-viewed jersey, and a 7-day
                      pulse. Sent every Monday at 09:00 IST to Legend-tier academies.
                    </p>
                    {updatedAt && (
                      <p className="text-[10px] text-slate-400 mt-1">
                        Last updated: {new Date(updatedAt).toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${enabled ? 'text-emerald-700' : 'text-slate-400'}`}>
                      {enabled ? 'ON' : 'OFF'}
                    </span>
                    <Switch
                      checked={enabled}
                      onCheckedChange={toggle}
                      disabled={saving}
                      data-testid="phase167b-toggle"
                    />
                  </div>
                </div>

                <div className={`rounded-xl border p-4 ${enabled ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
                  {enabled ? (
                    <div className="flex items-start gap-2 text-xs text-emerald-900">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                      <p>
                        You're subscribed. The next digest will land in your inbox on the
                        upcoming Monday morning, including only the past 7 days of scout
                        activity for your academy.
                      </p>
                    </div>
                  ) : (
                    <div className="text-xs text-amber-900">
                      <p>
                        Your weekly digest is paused. You'll still see the live heatmap on
                        the dashboard at any time — only the Monday-morning email is suppressed.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-2">
                  <p className="text-[11px] text-slate-400">
                    This preference is academy-scoped and applies to all owners.
                  </p>
                  <Button
                    variant="outline" size="sm"
                    onClick={load} disabled={loading}
                    data-testid="phase167b-refresh"
                    className="h-8 text-xs"
                  >
                    Refresh
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
